<template>
  <el-dialog
    :visible.sync="innerVisible"
    width="640px"
    custom-class="ca-confirm-dialog"
    :close-on-click-modal="true"
  >
    <div slot="title" class="ca-confirm-dialog__title">
      <i class="el-icon-success ca-confirm-dialog__icon" />
      <span>申请信息确认</span>
    </div>

    <!-- 快照信息：自绘带边框表格。
         注意：element-ui 2.x 没有 el-descriptions 组件（那是 element-plus 的），勿用 -->
    <div class="ca-confirm-dialog__table">
      <div v-for="row in rows" :key="row.key" class="ca-confirm-dialog__tr">
        <span class="ca-confirm-dialog__th">{{ row.label }}</span>
        <span class="ca-confirm-dialog__td">
          <span
            class="ca-confirm-dialog__value"
            :class="{
              'is-danger': row.key === 'total' || row.key === 'payType',
            }"
          >
            {{ rowValue(row.key) }}
          </span>
        </span>
      </div>
    </div>

    <div class="ca-confirm-dialog__pay-block">
      <div class="ca-confirm-dialog__pay-title">{{ payBlock.title }}</div>
      <template v-if="isOnline">
        <div class="ca-confirm-dialog__pay-row">
          <span class="ca-confirm-dialog__pay-label">{{ payBlock.amountLabel }}：</span>
          <span class="ca-confirm-dialog__pay-value is-danger">¥ {{ snapshot.total }}</span>
        </div>
        <div class="ca-confirm-dialog__pay-row">
          <span class="ca-confirm-dialog__pay-label">{{ payBlock.platformLabel }}：</span>
          <span class="ca-confirm-dialog__pay-value">{{ payBlock.platformValue }}</span>
        </div>
        <div class="ca-confirm-dialog__pay-row">
          <span class="ca-confirm-dialog__pay-label">{{ payBlock.descLabel }}：</span>
          <span class="ca-confirm-dialog__pay-value">{{ payBlock.descValue }}</span>
        </div>
      </template>
      <template v-else>
        <div v-loading="wireLoading">
          <div class="ca-confirm-dialog__pay-row">
            <span class="ca-confirm-dialog__pay-label">{{ payBlock.openNameLabel }}：</span>
            <span class="ca-confirm-dialog__pay-value">{{ payAccount.accountName || '--'}}</span>
          </div>
          <div class="ca-confirm-dialog__pay-row">
            <span class="ca-confirm-dialog__pay-label">{{ payBlock.openBankLabel }}：</span>
            <span class="ca-confirm-dialog__pay-value">{{ payAccount.openingBank || '--'}}</span>
          </div>
          <div class="ca-confirm-dialog__pay-row">
            <span class="ca-confirm-dialog__pay-label">{{ payBlock.openAccountLabel }}：</span>
            <span class="ca-confirm-dialog__pay-value">{{ payAccount.accountNo || '--' }}</span>
          </div>
          <div class="ca-confirm-dialog__pay-row">
            <span class="ca-confirm-dialog__pay-label">{{ payBlock.amountLabel }}：</span>
            <span class="ca-confirm-dialog__pay-value is-danger">¥ {{ snapshot.total }}</span>
          </div>
        </div>
      </template>
    </div>

    <div slot="footer">
      <el-button
        class="ca-confirm-dialog__cancel"
        @click="innerVisible = false"
      >
        取消
      </el-button>
      <el-button type="danger" :loading="confirming" @click="handleConfirm">
        确认
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { PAY_TYPE, CA_TYPE } from '../util/caForm'
import { getPayAccount } from '@/api/ca'

/**
 * 快照行定义（**数组顺序即渲染顺序**）—— 按 CA 类型区分。
 *
 * R1 已确认（2026-09-20，依据原型 `上海CA申请信息确认.png`）：
 *  - `CFCA` 版 **9 行**：第 7 行标签「被授权经办人」，含「申请单位」行
 *  - `SHCA` 版 **9 行**：第 6 行标签改为「经办人」，**无「申请单位」行**
 *    （原型确实未画，见细化设计 §6 / R5），并在该位置追加「收件人」「收件地址」；
 *    上海CA 不支持多数量申请（申请页隐藏数量板块、固定为 1），**无「申请数量」行**
 *
 * 行数为当前年限档支持加急时的满配行数；不支持加急时由 `rows` 计算属性剔除「是否加急」行。
 *
 * `key` → 实际取值字段的映射见细化设计 §6 的「取值来源」表；
 * 组件一律遍历本数组渲染，**不要写死行数或行标签**。
 */
const ROWS_BY_CA_TYPE = {
  CFCA: [
    { key: 'platform', label: '当前CA办理平台' },
    { key: 'bizType', label: '业务类型' },
    { key: 'number', label: '申请数量' },
    { key: 'limit', label: '申请年限' },
    { key: 'urgent', label: '是否加急' },
    { key: 'total', label: '总计' },
    { key: 'agent', label: '被授权经办人' },
    { key: 'org', label: '申请单位' },
    { key: 'payType', label: '支付方式' },
  ],
  SHCA: [
    { key: 'platform', label: '当前CA办理平台' },
    { key: 'bizType', label: '业务类型' },
    { key: 'limit', label: '申请年限' },
    { key: 'urgent', label: '是否加急' },
    { key: 'total', label: '总计' },
    { key: 'agent', label: '经办人' },
    { key: 'receiver', label: '收件人' },
    { key: 'postAddr', label: '收件地址' },
    { key: 'payType', label: '支付方式' },
  ],
}

/** 空值占位 */
const EMPTY_TEXT = '（未填写）'

/** 下半区块文案（网上支付信息 / 电汇信息 两套；电汇账户值由 payAccount 接口下发） */
const PAY_BLOCK = {
  online: {
    title: '网上支付信息',
    amountLabel: '应付金额',
    platformLabel: '支付平台',
    platformValue: '银联商务',
    descLabel: '支付说明',
    descValue: '确认后将跳转至收银台扫码支付',
  },
  wire: {
    title: '电汇信息',
    openNameLabel: '开户名称',
    openBankLabel: '开户行',
    openAccountLabel: '开户行账号',
    amountLabel: '应付金额',
  },
}

export default {
  name: 'CaConfirmDialog',
  props: {
    visible: { type: Boolean, default: false },
    // { caType, payType, platformName, bizTypeName, numberVal, limitName, urgentShow, isUrgent, total, form }
    snapshot: { type: Object, required: true },
    confirming: { type: Boolean, default: false },
  },
  data() {
    return {
      // 电汇收款账户（文档 §4.5 GuaranteePayAccountVo：accountName / openingBank / accountNo），弹框打开时实时拉取
      payAccount: {},
      wireLoading: false, // 收款账户拉取中 → 电汇区块显示 loading
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(v) {
        this.$emit('update:visible', v)
      },
    },
    // 是否网上支付（false = 电汇，弹框下半区块按此切换展示内容）
    isOnline() {
      return Number(this.snapshot.payType) === PAY_TYPE.ONLINE
    },
    // 快照行定义：CFCA 9 行 / 上海CA 9 行（无申请数量行），未识别的 caType 兜底用 CFCA 版。
    // 当前年限档不支持加急（申请页不展示加急板块，urgentShow 显式传 false）时剔除「是否加急」行
    rows() {
      const rows = ROWS_BY_CA_TYPE[this.snapshot.caType] || ROWS_BY_CA_TYPE.CFCA
      return this.snapshot.urgentShow === false
        ? rows.filter((row) => row.key !== 'urgent')
        : rows
    },
    // 下半区块文案（网上支付信息 / 电汇信息 两套）
    payBlock() {
      return this.isOnline ? PAY_BLOCK.online : PAY_BLOCK.wire
    },
  },
  watch: {
    visible(v) {
      // 电汇分支：弹框每次打开都实时调 payAccount 取最新收款账户（无入参，与订单无关）。
      // 账户信息可能变更，刻意不做页面级缓存，避免用户打款到已失效的账户
      if (v && !this.isOnline) this.loadPayAccount()
    },
  },
  methods: {
    loadPayAccount() {
      this.wireLoading = true
      getPayAccount()
        .then((res) => {
          this.payAccount = res.data || {}
        })
        .finally(() => {
          this.wireLoading = false
        })
    },
    rowValue(key) {
      const s = this.snapshot
      const f = s.form || {}
      const empty = EMPTY_TEXT
      switch (key) {
        case 'platform':
          return s.platformName || empty
        case 'bizType':
          return s.bizTypeName || empty
        case 'number':
          return s.numberVal
        case 'limit':
          return s.limitName || empty
        case 'urgent':
          return s.isUrgent ? '需要' : '不需要'
        case 'total':
          return '¥ ' + s.total
        case 'agent':
          // CFCA「被授权经办人」/ 上海CA「经办人」都取 linkMan（姓名 / 手机号 两段拼接）
          return (f.linkMan || empty) + ' / ' + (f.linkTel || empty)
        case 'org':
          return f.applyOrgName || empty
        case 'receiver':
          // 上海CA 的「收件人」行只展示姓名一段，不与手机号拼接（原型即如此）
          return f.receiverName || empty
        case 'postAddr':
          return f.postAddr || empty
        case 'payType':
          return this.isOnline ? '网上支付' : '电汇'
        default:
          return empty
      }
    },
    handleConfirm() {
      this.$emit('confirm')
    },
  },
}
</script>
